import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { LuMail, LuPhone, LuBuilding, LuUser, LuPencil, LuTrash, LuLinkedin, LuGlobe } from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

// Utility function to mask email addresses
const maskEmail = (email) => {
  if (!email || typeof email !== 'string') return '';
  const [username, domain] = email.split('@');
  if (!username || !domain) return email;

  // Show first letter and mask the rest
  const maskedUsername = username.charAt(0) + '*'.repeat(username.length - 1);
  return `${maskedUsername}@${domain}`;
};

// Utility function to mask phone numbers
const maskPhone = (phone) => {
  if (!phone || typeof phone !== 'string') return '';
  // Show first 3 digits and mask the rest
  if (phone.length <= 3) return phone;
  return phone.substring(0, 3) + '*'.repeat(phone.length - 3);
};

const ContactCard = ({ contact, onEdit, onDelete, className = '' }) => {
  const { userProfile } = useAuth();

  // Check if current user can view unmasked contact info
  const canViewUnmasked = () => {
    // Admins can see everything
    if (userProfile?.role === 'Admin') return true;

    // Contact creator can see their own contacts
    if (userProfile?.id === contact.created_by_user_id) return true;

    // Otherwise, apply privacy masking
    return false;
  };

  // Determine what information to display
  const displayName = `${contact.first_name} ${contact.last_name}`.trim();
  const displayEmail = contact.is_private && !canViewUnmasked()
    ? maskEmail(contact.email)
    : contact.email;
  const displayPhone = contact.is_private && !canViewUnmasked()
    ? maskPhone(contact.phone)
    : contact.phone;

  const canEditOrDelete = () => {
    if (userProfile?.role === 'Admin') return true;
    if (userProfile?.id === contact.created_by_user_id) return true;
    return false;
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow ${className}`}>
      {/* Contact Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <SafeIcon icon={LuUser} className="h-10 w-10 text-gray-400 bg-gray-100 rounded-full p-2" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">{displayName}</h3>
            {(contact.job_title || contact.department) && (
              <p className="text-sm text-gray-500">
                {contact.job_title}
                {contact.job_title && contact.department && ' • '}
                {contact.department}
              </p>
            )}
            {contact.is_private && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                VIP Contact
              </span>
            )}
          </div>
        </div>
        {canEditOrDelete() && (
          <div className="flex space-x-2">
            <button
              onClick={() => onEdit(contact)}
              className="text-gray-400 hover:text-blue-500 transition-colors"
              title="Edit Contact"
            >
              <SafeIcon icon={LuPencil} className="h-4 w-4" />
            </button>
            <button
              onClick={() => onDelete(contact.id)}
              className="text-gray-400 hover:text-red-500 transition-colors"
              title="Delete Contact"
            >
              <SafeIcon icon={LuTrash} className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Contact Information */}
      <div className="space-y-3">
        {displayEmail && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <SafeIcon icon={LuMail} className="h-4 w-4 text-gray-400" />
            <span className={contact.is_private && !canViewUnmasked() ? 'italic text-gray-400' : ''}>
              {displayEmail}
            </span>
          </div>
        )}

        {displayPhone && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <SafeIcon icon={LuPhone} className="h-4 w-4 text-gray-400" />
            <span className={contact.is_private && !canViewUnmasked() ? 'italic text-gray-400' : ''}>
              {displayPhone}
            </span>
          </div>
        )}

        {contact.accounts && (
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <SafeIcon icon={LuBuilding} className="h-4 w-4 text-gray-400" />
            <span>{contact.accounts.name}</span>
            {contact.accounts.industry && (
              <span className="text-gray-400">• {contact.accounts.industry}</span>
            )}
          </div>
        )}

        {(contact.linkedin_url || contact.website_url) && (
          <div className="flex items-center space-x-4 pt-2">
            {contact.linkedin_url && (
              <a
                href={contact.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600 transition-colors"
                title="LinkedIn Profile"
              >
                <SafeIcon icon={LuLinkedin} className="h-4 w-4" />
              </a>
            )}
            {contact.website_url && (
              <a
                href={contact.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600 transition-colors"
                title="Website"
              >
                <SafeIcon icon={LuGlobe} className="h-4 w-4" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* Privacy Indicator */}
      {contact.is_private && !canViewUnmasked() && (
        <div className="mt-4 p-2 bg-gray-50 rounded-md">
          <p className="text-xs text-gray-500 text-center">
            Contact information is restricted. Only the creator and administrators can view full details.
          </p>
        </div>
      )}

      {/* Metadata */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>Created: {new Date(contact.created_at).toLocaleDateString()}</span>
          {contact.updated_at !== contact.created_at && (
            <span>Updated: {new Date(contact.updated_at).toLocaleDateString()}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactCard;